// ============================================
// MyPage - Avatar Module (ES6)
// 아바타 업로드, 크롭, 캐시
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { getDb } from './state.js';
import { USER_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCurrentUser } from '../app/state.js';

// ============================================
// State
// ============================================

let cachedAvatarUrl = null;
let cachedAvatarPosition = 'center 20%';
let avatarLoaded = false;
let avatarImageData = null;
let avatarObjectPosition = 'center 20%';

// ============================================
// Avatar Upload Modal
// ============================================

export function openAvatarUploadModal() {
  if (!getCurrentUser()) {
    showToast('로그인이 필요합니다', 'error');
    return;
  }

  const modal = document.createElement('div');
  modal.id = 'avatarUploadModal';
  modal.className = 'bottom-sheet-overlay';
  modal.innerHTML = `
    <div class="bottom-sheet-backdrop"></div>
    <div class="bottom-sheet" style="max-height: 90vh;">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-header">
        <h3>프로필 사진 변경</h3>
      </div>
      <div class="bottom-sheet-body">
        <div class="avatar-preview-container" id="avatarPreviewContainer">
          <div class="avatar-preview-circle" id="avatarPreviewCircle">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" stroke-width="1.5">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
        </div>

        <div id="avatarPositionControl" style="display: none; margin-top: 16px;">
          <label style="font-size: 12px; color: var(--text-secondary); display: block; margin-bottom: 8px;">
            이미지 위치 조절 (위/아래로 드래그)
          </label>
          <input type="range" id="avatarPositionSlider" min="0" max="100" value="20"
            class="chat-settings-slider" style="width: 100%;">
          <div style="display: flex; justify-content: space-between; font-size: 11px; color: var(--text-tertiary); margin-top: 4px;">
            <span>위</span><span>가운데</span><span>아래</span>
          </div>
        </div>

        <input type="file" id="avatarFileInput" accept="image/*" style="display: none;">

        <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 20px;">
          <button class="btn btn-primary avatar-select-btn" style="width: 100%;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            사진 선택
          </button>
          <button id="avatarSaveBtn" class="btn btn-secondary" style="width: 100%; display: none;">
            저장
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // 이벤트 바인딩 (onclick 대체)
  modal.querySelector('.bottom-sheet-backdrop').addEventListener('click', closeAvatarUploadModal);
  modal.querySelector('.avatar-select-btn').addEventListener('click', () => {
    document.getElementById('avatarFileInput').click();
  });
  modal.querySelector('#avatarFileInput').addEventListener('change', handleAvatarFileSelect);
  modal.querySelector('#avatarPositionSlider').addEventListener('input', (e) => {
    updateAvatarPosition(e.target.value);
  });
  modal.querySelector('#avatarSaveBtn').addEventListener('click', saveAvatarImage);

  requestAnimationFrame(() => {
    modal.classList.add('open');
  });
}

export function closeAvatarUploadModal() {
  const modal = document.getElementById('avatarUploadModal');
  if (modal) {
    modal.classList.remove('open');
    setTimeout(() => modal.remove(), 300);
  }
  avatarImageData = null;
}

export function handleAvatarFileSelect(event) {
  const file = event.target.files[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    showToast('이미지 파일만 선택할 수 있습니다', 'error');
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    showToast('5MB 이하의 이미지만 업로드할 수 있습니다', 'error');
    return;
  }

  const reader = new FileReader();
  reader.onload = function(e) {
    avatarImageData = e.target.result;

    const previewCircle = document.getElementById('avatarPreviewCircle');
    previewCircle.innerHTML = `<img id="avatarPreviewImg" src="${avatarImageData}" alt="미리보기" style="width: 100%; height: 100%; object-fit: cover; object-position: center 20%;">`;

    document.getElementById('avatarPositionControl').style.display = 'block';
    document.getElementById('avatarPositionSlider').value = 20;
    document.getElementById('avatarSaveBtn').style.display = 'block';

    avatarObjectPosition = 'center 20%';
  };
  reader.readAsDataURL(file);
}

export function updateAvatarPosition(value) {
  const img = document.getElementById('avatarPreviewImg');
  if (img) {
    avatarObjectPosition = `center ${value}%`;
    img.style.objectPosition = avatarObjectPosition;
  }
}

export async function saveAvatarImage() {
  const user = getCurrentUser();
  if (!avatarImageData || !user) return;

  showGlobalLoading();

  try {
    const response = await authFetch(USER_ENDPOINTS.uploadUserAvatar, {
      body: {
        imageData: avatarImageData,
        cropInfo: { objectPosition: avatarObjectPosition }
      }
    });

    if (!response.ok) throw new Error('업로드 실패');

    const result = await response.json();

    if (result.avatarUrl) {
      updateAvatarCache(result.avatarUrl, avatarObjectPosition);
      const avatarEl = document.getElementById('myPageAvatarImage');
      if (avatarEl) {
        avatarEl.innerHTML = `<img src="${result.avatarUrl}" alt="프로필" style="width: 100%; height: 100%; object-fit: cover; object-position: ${avatarObjectPosition};">`;
      }
    }

    hideGlobalLoading();
    closeAvatarUploadModal();
    showToast('프로필 사진이 변경되었습니다');

  } catch (e) {
    hideGlobalLoading();
    showToast('업로드 실패: ' + e.message, 'error');
  }
}

// ============================================
// Avatar Loading & Cache
// ============================================

export async function loadUserAvatar() {
  const user = getCurrentUser();
  if (!user) return;

  const avatarEl = document.getElementById('myPageAvatarImage');
  if (!avatarEl) return;

  if (avatarLoaded && cachedAvatarUrl) {
    avatarEl.innerHTML = `<img src="${cachedAvatarUrl}" alt="프로필" style="width: 100%; height: 100%; object-fit: cover; object-position: ${cachedAvatarPosition};">`;
    return;
  }

  if (!avatarLoaded) {
    avatarEl.innerHTML = `<div class="avatar-loading-spinner"><div class="spinner" style="width: 20px; height: 20px; border-width: 2px;"></div></div>`;
  }

  try {
    const db = getDb();
    const userDoc = await db.collection('users').doc(user.uid).get();

    if (userDoc.exists) {
      const data = userDoc.data();
      if (data.avatarUrl) {
        cachedAvatarUrl = data.avatarUrl;
        cachedAvatarPosition = data.avatarCrop?.objectPosition || 'center 20%';
        avatarLoaded = true;

        const img = new Image();
        img.onload = () => {
          avatarEl.innerHTML = `<img src="${cachedAvatarUrl}" alt="프로필" style="width: 100%; height: 100%; object-fit: cover; object-position: ${cachedAvatarPosition};">`;
        };
        img.onerror = () => {
          avatarEl.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>`;
        };
        img.src = cachedAvatarUrl;
      } else {
        avatarLoaded = true;
        avatarEl.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>`;
      }
    } else {
      avatarLoaded = true;
    }
  } catch (e) {
    console.error('[MyPage/Avatar] 로드 오류:', e);
    avatarLoaded = true;
  }
}

function updateAvatarCache(url, position) {
  cachedAvatarUrl = url;
  cachedAvatarPosition = position || 'center 20%';
  avatarLoaded = true;
}
